function requireBrowserApi(name, value) {
  if (!value) {
    throw new Error(`${name} is unavailable. Merch artwork exports must run in a browser.`);
  }
}

function downloadBlob(blob, filename) {
  requireBrowserApi("document", typeof document !== "undefined" && document);
  requireBrowserApi("URL.createObjectURL", typeof URL !== "undefined" && URL.createObjectURL);

  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function downloadSvg(svgString, filename) {
  if (!svgString || typeof svgString !== "string") {
    throw new Error("downloadSvg requires a serialized SVG string.");
  }
  if (!filename) {
    throw new Error("downloadSvg requires a filename.");
  }

  const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
  downloadBlob(svgBlob, filename);
}

function loadSvgImage(svgString) {
  return new Promise((resolve, reject) => {
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const objectUrl = URL.createObjectURL(svgBlob);
    const image = new Image();

    image.onload = () => resolve({ image, objectUrl });
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("The SVG or one of its embedded artwork assets could not be loaded."));
    };
    image.src = objectUrl;
  });
}

function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("The browser could not encode the artwork as PNG."));
      }, "image/png");
    } catch (error) {
      reject(new Error(`PNG encoding failed: ${error.message}`));
    }
  });
}

export async function exportSvgToPng({
  svgString,
  filename,
  width = 4500,
  height = 5400
}) {
  if (!svgString || typeof svgString !== "string") {
    throw new Error("exportSvgToPng requires a serialized SVG string.");
  }
  if (!filename) {
    throw new Error("exportSvgToPng requires a filename.");
  }
  if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) {
    throw new Error("PNG width and height must be positive integers.");
  }

  requireBrowserApi("document", typeof document !== "undefined" && document);
  requireBrowserApi("URL.createObjectURL", typeof URL !== "undefined" && URL.createObjectURL);
  requireBrowserApi("Image", typeof Image !== "undefined" && Image);

  let objectUrl;
  try {
    const loaded = await loadSvgImage(svgString);
    objectUrl = loaded.objectUrl;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("The browser could not create a 2D canvas context.");

    context.clearRect(0, 0, width, height);
    context.drawImage(loaded.image, 0, 0, width, height);
    const pngBlob = await canvasToPngBlob(canvas);
    downloadBlob(pngBlob, filename);
    return pngBlob;
  } catch (error) {
    throw new Error(`Unable to export merch artwork PNG: ${error.message}`);
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}

// The file is exported at the required production pixel dimensions.
// Physical print size and DPI interpretation are set during Printful placement.
